import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'

import { IconDollarCircleArrowsOutline } from 'assets/svg/sprite'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    deleteSubscriptionPlan,
    getSubscriptionPlans,
    updateSubscriptionPlans
} from 'services/endpoints/settings/general/subscription'
import { useUserProfile } from 'store'
import { type PaymentPlan } from 'types/api/subscription'

import style from './_discounted-bundles.module.scss'
import { SubscriptionCard } from '../components/SubscriptionCard'
import { NewPlan } from '../NewPlan'


export const DiscountedBundles = () => {
    const { t } = useTranslation()
    const userData = useUserProfile()!
    const modalData = useModalContext()
    const queryClient = useQueryClient()
    const initialPrice = queryClient
        .getQueryData<any>(['allSubsPlans', userData.id])
        ?.subscriptions.filter((bundle: any) => bundle.month_count === 1)[0]
        .price
    const [singleMonthPrice, setSingleMonthPrice] = useState(initialPrice || 0)

    const { data, isLoading } = useQuery(
        ['allSubsPlans', userData.id],
        async () => await getSubscriptionPlans(userData.id),
        {
            onSuccess: (resp: any) => {
                const price = resp.subscriptions.filter(
                    (bundle: any) => bundle.month_count === 1
                )[0].price
                setSingleMonthPrice(price)
            }
        }
    )

    const updatePlan = useMutation(
        async (planData: {
            newPlan: PaymentPlan
            index: number
            toggleUpdate?: boolean
        }) => {
            return await updateSubscriptionPlans(planData.newPlan)
        },
        {
            onMutate: async resData => {
                await queryClient.cancelQueries(['allSubsPlans', userData.id])
                const previousValue = await queryClient.getQueryData([
                    'allSubsPlans',
                    userData.id
                ])

                queryClient.setQueryData(
                    ['allSubsPlans', userData.id],
                    (oldData: any) => {
                        const newArray = oldData.subscriptions
                        if (resData.toggleUpdate) {
                            for (let i = 0; i < newArray.length; i++) {
                                if (
                                    newArray[i].month_count ===
                                    resData.newPlan.month_count
                                ) {
                                    newArray[i].active =
                                        newArray[i].active === 1 ? 0 : 1
                                }
                            }
                        }
                        newArray[resData.index] = resData.newPlan

                        return { ...oldData, subscriptions: newArray }
                    }
                )

                return previousValue
            },
            onError: (error, _, context: any) => {
                console.error(error)

                queryClient.setQueryData(
                    ['allSubsPlans', userData.id],
                    context.previousQueryData
                )
            }
        }
    )

    const deletePlan = useMutation(
        async (planId: number) => {
            return await deleteSubscriptionPlan(planId)
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries(['allSubsPlans', userData.id])
            }
        }
    )

    const editPlan = (id?: string) =>
        id &&
        modalData.addModal(
            t('updateSubscriptionPlan'),
            <NewPlan id={id} price={singleMonthPrice} />
        )

    return (
        <WithHeaderSection
            customClass={style.header_wrapper}
            headerSection={
                <LayoutHeader
                    type='basic'
                    title={t('discountedBundles')}
                    section={t('subscriptions')}
                />
            }
        >
            {isLoading && <div className='loader'></div>}
            {!isLoading && data.subscriptions && singleMonthPrice > 0
                ? data.subscriptions
                      .filter((bundle: any) => bundle.month_count !== 1)
                      .sort((a: any, b: any) => a.month_count - b.month_count)
                      .map((bundle: any) => (
                          <SubscriptionCard
                              type='card'
                              id={bundle.id}
                              key={bundle.id}
                              name={bundle.name}
                              price={bundle.price}
                              monthCount={bundle.month_count}
                              discount={bundle.discount}
                              displayMonthlyPrice={true}
                              isActive={bundle.active === 1}
                              customClass={style.bundles_card}
                              selectable={false}
                              editFn={() => editPlan(bundle.id)}
                              deleteFn={() => {
                                  deletePlan.mutate(bundle.id)
                              }}
                              switchFn={() => {
                                  updatePlan.mutate({
                                      newPlan: {
                                          ...bundle,
                                          active: bundle.active === 1 ? 0 : 1
                                      },
                                      index: data.subscriptions.indexOf(bundle),
                                      toggleUpdate: true
                                  })
                              }}
                          />
                      ))
                : !isLoading && (
                      <div className={style.no_price_container}>
                          <IconDollarCircleArrowsOutline />
                          <div className={style.no_price_text}>
                              You must have a subscription price enabled in
                              order to create discount bundles.
                          </div>
                          <Link to='/settings/general/subscription-options/set-price'>
                              <div className={style.no_price_link}>
                                  Goto Subscription Pricing
                              </div>
                          </Link>
                      </div>
                  )}
            {!isLoading && singleMonthPrice > 0 && (
                <SubscriptionCard
                    clickFn={() => {
                        modalData.addModal(
                            'Create New Bundle',
                            <NewPlan price={singleMonthPrice} />
                        )
                    }}
                />
            )}
        </WithHeaderSection>
    )
}
